import { supabase } from '@/lib/supabase';

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
  status: number;
}

interface TokenState {
  accessToken: string | null;
  refreshPromise: Promise<string | null> | null;
  lastRefreshAttempt: number;
}

const tokenState: TokenState = {
  accessToken: null,
  refreshPromise: null,
  lastRefreshAttempt: 0,
};

// C3 (SV2-13, CL-26, CL-P10): single-attempt refresh. ONE refresh+retry per
// request — no recursion, no retry storm. Terminal 401 fires the
// auth-expired callback (AuthContext signs the user out). Auth-retry and
// network-retry are tracked as independent flags via RetryState, not a
// shared counter, so they cannot consume each other's single-shot budget.
const TOKEN_EXPIRY_BUFFER_MS = 5 * 60 * 1000; // Refresh if token expires within 5 minutes

// C3: typed terminal-auth error. Currently authenticatedFetch returns the
// existing { success:false, status:401 } shape so existing callers don't
// break, AND fires the global auth-expired callback (AuthContext logout).
// New callers that prefer try/catch can `throw new SessionExpiredError()`
// in their own wrappers; the class is exported so a single instanceof
// check works everywhere.
export class SessionExpiredError extends Error {
  readonly code = 'SESSION_EXPIRED';
  constructor(message = 'Authentication expired. Please log in again.') {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

// ---------------------------------------------------------------------------
// Auth-expired callback — fired when a 401 cannot be recovered after all
// token refresh retries. AuthContext registers a handler that force-signs the
// user out and shows an alert. Cleared on sign-out so it never fires for an
// already-logged-out session.
// ---------------------------------------------------------------------------
let _authExpiredCallback: (() => void) | null = null;
let _lastAuthExpiredFiredAt = 0;
const AUTH_EXPIRED_COOLDOWN_MS = 30_000; // prevent concurrent 401 storms from firing multiple times

export function setAuthExpiredCallback(fn: (() => void) | null): void {
  _authExpiredCallback = fn;
}

function fireAuthExpired(): void {
  const now = Date.now();
  if (!_authExpiredCallback || now - _lastAuthExpiredFiredAt < AUTH_EXPIRED_COOLDOWN_MS) return;
  _lastAuthExpiredFiredAt = now;
  _authExpiredCallback();
}

/**
 * Check if a JWT token is expired or expiring soon (within buffer)
 * This is a LOCAL check - no API call needed
 */
function isTokenExpiredOrExpiringSoon(token: string): boolean {
  try {
    const parts = token.split('.');
    if (parts.length !== 3 || !parts[1]) {
      return true; // Invalid token format
    }
    const payload = JSON.parse(atob(parts[1]));
    const expiryMs = payload.exp * 1000;
    const now = Date.now();
    const isExpiringSoon = (expiryMs - now) < TOKEN_EXPIRY_BUFFER_MS;
    if (isExpiringSoon) {
      console.log(`🕐 Token ${now >= expiryMs ? 'EXPIRED' : 'expiring soon'}: expires ${new Date(expiryMs).toISOString()}`);
    }
    return isExpiringSoon;
  } catch (e) {
    console.warn('⚠️ Could not parse token expiry, assuming expired');
    return true;
  }
}

/**
 * Get fresh access token from Supabase session.
 * PROACTIVELY refreshes if token is expired or expiring soon.
 */
export async function getAccessToken(): Promise<string | null> {
  // If we have an in-memory token, check if it's still valid
  if (tokenState.accessToken) {
    // Proactive check: if token is expired or expiring soon, refresh BEFORE using
    if (isTokenExpiredOrExpiringSoon(tokenState.accessToken)) {
      console.log('🔄 Token expiring soon, proactively refreshing...');
      const newToken = await refreshAccessToken();
      if (newToken) {
        return newToken;
      }
      // If refresh failed, still try the old token - server will reject if truly expired
      console.log('⚠️ Proactive refresh failed, using existing token');
    }
    return tokenState.accessToken;
  }

  // Fallback: Get from Supabase directly
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error || !session?.access_token) {
      console.warn('⚠️ No access token available from Supabase session');
      // Try to refresh anyway - might have refresh_token
      const refreshedToken = await refreshAccessToken();
      if (refreshedToken) {
        return refreshedToken;
      }
      return null;
    }
    
    // Check if this token is also expiring soon
    if (isTokenExpiredOrExpiringSoon(session.access_token)) {
      console.log('🔄 Session token expiring soon, refreshing...');
      const newToken = await refreshAccessToken();
      if (newToken) {
        return newToken;
      }
    }
    
    // Cache in memory for subsequent calls
    tokenState.accessToken = session.access_token;
    return session.access_token;
  } catch (error) {
    console.error('❌ Error getting access token from Supabase:', error);
    // Last resort: try refresh
    const refreshedToken = await refreshAccessToken();
    return refreshedToken;
  }
}

/**
 * Set access token in memory - called by AuthContext when session changes
 */
export function setAccessToken(token: string | null): void {
  tokenState.accessToken = token;
}

/**
 * Clear cached token - called on sign out
 */
export function clearAccessToken(): void {
  tokenState.accessToken = null;
  tokenState.refreshPromise = null;
}

/**
 * Force refresh the access token via Supabase
 * More resilient with retry logic
 */
export async function refreshAccessToken(force = false): Promise<string | null> {
  const now = Date.now();

  // Concurrent-callers dedupe: any caller, forced or not, must join the
  // in-flight refresh rather than fire a parallel one.
  if (tokenState.refreshPromise) {
    console.log('⏳ Refresh already in progress, waiting...');
    return tokenState.refreshPromise;
  }

  // Proactive callers (preflight expiry check) get rate-limited to 2s so
  // back-to-back API calls don't spam Supabase. The 401-reactive path
  // passes force=true so a server rejection ALWAYS triggers a real refresh
  // — otherwise a recent proactive refresh + a stale-token 401 would
  // short-circuit to the cached token and falsely log the user out.
  if (!force && now - tokenState.lastRefreshAttempt < 2000) {
    console.log('⏳ Refresh rate limited, using existing token');
    return tokenState.accessToken;
  }

  tokenState.lastRefreshAttempt = now;

  // C3 (CL-26): SINGLE refresh attempt. Previous design looped 3x with
  // backoff; combined with authenticatedFetch's 5x retry that produced a
  // 15-call refresh storm against Supabase per failed request. Now: one
  // attempt, 8s timeout, and either we have a new token or we don't.
  tokenState.refreshPromise = (async () => {
    try {
      console.log('🔄 Refreshing access token via Supabase (single attempt)...');

      const timeoutPromise = new Promise<null>((resolve) =>
        setTimeout(() => {
          console.warn('⚠️ Token refresh timed out after 8s');
          resolve(null);
        }, 8000)
      );

      const refreshPromise = supabase.auth.refreshSession();
      const result = await Promise.race([refreshPromise, timeoutPromise]);

      if (result && 'data' in result && result.data.session?.access_token) {
        const newToken = result.data.session.access_token;
        tokenState.accessToken = newToken;
        console.log('✅ Token refreshed successfully');
        return newToken;
      }

      console.warn('⚠️ Token refresh returned no new token');
      return null;
    } catch (error) {
      console.error('❌ Token refresh error:', error);
      return null;
    }
  })();

  return tokenState.refreshPromise.finally(() => {
    tokenState.refreshPromise = null;
  });
}

/**
 * Make an authenticated API request with automatic token refresh on 401.
 * C3: auth-retry and network-retry are tracked separately so a transient
 * network blip can never consume the single allowed 401 refresh attempt.
 */
interface RetryState {
  /** True once we've already done one refresh+retry on a 401. */
  authRetried?: boolean;
  /** True once we've already done one network-error retry. */
  networkRetried?: boolean;
}

export async function authenticatedFetch<T = any>(
  url: string,
  options: RequestInit = {},
  retryState: RetryState = {}
): Promise<ApiResponse<T>> {
  let token = await getAccessToken();
  
  if (!token) {
    console.warn('⚠️ No access token available for API call');
    return {
      success: false,
      error: 'No authentication token available',
      status: 401,
    };
  }

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...options.headers,
  };

  try {
    const timeoutPromise = new Promise<Response>((_, reject) => 
      setTimeout(() => reject(new Error('Request timeout after 15s')), 15000)
    );

    const fetchPromise = fetch(url, {
      ...options,
      headers,
    });

    const response = await Promise.race([fetchPromise, timeoutPromise]);

    // C3 (CL-26, CL-P10): single 401 → refresh → retry-once attempt. After
    // one retry, a 401 is terminal: fire the auth-expired callback (which
    // AuthContext wires to a sign-out alert) and return 401. NO recursion.
    // The auth-retry flag is INDEPENDENT of network-retry, so a transient
    // network blip cannot consume the single allowed 401 refresh attempt.
    if (response.status === 401 && !retryState.authRetried) {
      console.log('🔄 Got 401, attempting single token refresh...');
      // C3: force=true bypasses the 2s proactive rate-limit guard. A 401
      // is the server saying "your token is rejected" — we MUST attempt a
      // real refresh, not return the cached value.
      const newToken = await refreshAccessToken(true);

      if (newToken && newToken !== token) {
        return authenticatedFetch<T>(url, options, { ...retryState, authRetried: true });
      }

      console.warn('⚠️ Token refresh did not produce new token, returning 401');
      fireAuthExpired();
      return {
        success: false,
        error: 'Authentication expired. Please log in again.',
        status: 401,
      };
    }

    if (response.status === 401) {
      // Already retried once with a refresh; terminal — fire global logout.
      console.warn('⚠️ Got 401 after refresh+retry, firing auth-expired');
      fireAuthExpired();
    }

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      return {
        success: false,
        error: data.error || data.message || `HTTP ${response.status}`,
        code: data.code,
        status: response.status,
      };
    }

    return {
      success: true,
      data,
      status: response.status,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    
    // Network errors — single retry for transient RN connectivity blips.
    // Distinct from auth retry (CL-26 governs auth refresh storms only).
    // Tracked via its own flag so it doesn't consume the auth-retry budget.
    if (errorMessage.includes('Network') && !retryState.networkRetried) {
      console.log('🔄 Network error, single retry...');
      await new Promise(resolve => setTimeout(resolve, 500));
      return authenticatedFetch<T>(url, options, { ...retryState, networkRetried: true });
    }
    
    console.error('❌ API request failed:', error);
    
    // Timeout or exhausted retries
    if (errorMessage.includes('timeout') || errorMessage.includes('Network')) {
      return {
        success: false,
        error: 'Network error. Please check your connection.',
        status: 0,
      };
    }

    return {
      success: false,
      error: errorMessage,
      status: 0,
    };
  }
}

// The mobile app always talks to the canonical production API.
// Do not allow a workspace/EAS EXPO_PUBLIC_API_URL value to override this:
// Metro inlines EXPO_PUBLIC_* variables into development builds, so a stale
// Replit development URL can silently route only apiService callers away from
// production while hardcoded callers continue to work.
const API_BASE_URL = 'https://mobile.padrinho.app';

export const apiUrl = (path: string): string => {
  return `${API_BASE_URL}${path}`;
};

// React Native's `fetch` requires absolute URLs — a bare `/api/...` path
// resolves to nothing on device and surfaces as a generic "Network error"
// (this is exactly what was breaking the post-call CSAT submission). Treat
// any input starting with `/` as a server path and prepend the base; pass
// fully-qualified URLs through unchanged so callers hitting third-party
// services aren't double-prefixed.
const resolveUrl = (urlOrPath: string): string =>
  urlOrPath.startsWith('http://') || urlOrPath.startsWith('https://')
    ? urlOrPath
    : apiUrl(urlOrPath);

// Convenience functions for different HTTP methods
export const apiGet = <T = any>(urlOrPath: string, options?: RequestInit) =>
  authenticatedFetch<T>(resolveUrl(urlOrPath), { ...options, method: 'GET' });

export const apiPost = <T = any>(urlOrPath: string, body: any, options?: RequestInit) =>
  authenticatedFetch<T>(resolveUrl(urlOrPath), {
    ...options,
    method: 'POST',
    body: JSON.stringify(body),
  });

export const apiPut = <T = any>(urlOrPath: string, body: any, options?: RequestInit) =>
  authenticatedFetch<T>(resolveUrl(urlOrPath), {
    ...options,
    method: 'PUT',
    body: JSON.stringify(body),
  });

export const apiDelete = <T = any>(urlOrPath: string, options?: RequestInit) =>
  authenticatedFetch<T>(resolveUrl(urlOrPath), { ...options, method: 'DELETE' });

// Backward-compatible apiService object for existing code
export const apiService = {
  get: <T = any>(path: string, options?: RequestInit) => 
    authenticatedFetch<T>(apiUrl(path), { ...options, method: 'GET' }),
  
  post: <T = any>(path: string, body?: any, options?: RequestInit) => {
    const requestOptions: RequestInit = { ...options, method: 'POST' };
    if (body !== undefined) {
      requestOptions.body = JSON.stringify(body);
    }
    return authenticatedFetch<T>(apiUrl(path), requestOptions);
  },
  
  put: <T = any>(path: string, body?: any, options?: RequestInit) => {
    const requestOptions: RequestInit = { ...options, method: 'PUT' };
    if (body !== undefined) {
      requestOptions.body = JSON.stringify(body);
    }
    return authenticatedFetch<T>(apiUrl(path), requestOptions);
  },
  
  delete: <T = any>(path: string, options?: RequestInit) => 
    authenticatedFetch<T>(apiUrl(path), { ...options, method: 'DELETE' }),

  patch: <T = any>(path: string, body?: any, options?: RequestInit) => {
    const requestOptions: RequestInit = { ...options, method: 'PATCH' };
    if (body !== undefined) {
      requestOptions.body = JSON.stringify(body);
    }
    return authenticatedFetch<T>(apiUrl(path), requestOptions);
  },
};

// Plus status types and functions for useServerVerifiedPlus hook
export interface PlusStatusResponse {
  success: boolean;
  isPlus: boolean;
  expirationDate?: string;
  error?: string;
}

// P02-6: Module-scoped plusStatusCache removed. The server LRU (Phase 4) is the
// authoritative cache; React component state in useServerVerifiedPlus is the sole
// client-side representation — no persistent module-level silo needed.
export async function getServerPlusStatus(): Promise<PlusStatusResponse> {
  try {
    const response = await apiService.get<PlusStatusResponse>('/api/plus/status');
    if (response.success && response.data) {
      return response.data;
    }
    return { success: false, isPlus: false, error: response.error || 'Unknown error' };
  } catch (error) {
    return { success: false, isPlus: false, error: 'Failed to check subscription status' };
  }
}

export async function refreshServerPlusStatus(): Promise<PlusStatusResponse> {
  // Server-side LRU (P02 Phase 4) handles cache invalidation on the server.
  // Client simply re-fetches; server decides whether to revalidate RevenueCat.
  return getServerPlusStatus();
}

export async function startTrail(slug: string): Promise<void> {
  try {
    await apiService.post(`/api/trails/${encodeURIComponent(slug)}/start`);
  } catch (err) {
    console.warn('startTrail: fire-and-forget failed', err);
  }
}
